import React, { useMemo } from 'react';
import Text from '../../shared/Text';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import styled from 'styled-components';
import ScreenWrapper from '../../shared/ScreenWrapper';

import styles from '../../../utility/styles';
import RolloutChart from './RolloutChart';
import WorldMap from './WorldMap';
import PreviousVersions from './PreviousVersions';
import ReleaseNotes from './ReleaseNotes';
import { formatDate } from '../../../utility/helpers';

const colors = styles.colors.software_tracker_colors

function GeistFonts() {
    React.useEffect(() => {
        const fonts = [
            'https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap',
            'https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500;600;700&display=swap'
        ]
        fonts.forEach(href => {
            if (!document.querySelector(`link[href="${href}"]`)) {
                const link = document.createElement('link')
                link.rel = 'stylesheet'
                link.href = href
                document.head.appendChild(link)
            }
        })
    }, [])
    return null
}

const StyledContainer = styled(Container)`
    margin-top: 75px;
    margin-bottom: 75px;
    max-width: 100%;
    font-family: ${styles.fonts.geist};
`

const StyledErrorContainer = styled(Container)`
    margin-top: 75px;
    margin-bottom: 75px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const BackLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${styles.colors.lightGray};
    font-size: 13px;
    font-weight: 500;
    font-family: ${styles.fonts.geist};
    margin-bottom: 24px;
    text-decoration: none;
    &:hover {
        color: ${styles.colors.white};
    }
`

const HeaderRow = styled.div`
    display: flex;
    justify-content: ${({ justify }) => justify || 'flex-start'};
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: ${({ margin }) => margin || '4px'};
`

const FleetStat = styled.div`
    display: flex;
    padding-left: 24px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    align-self: stretch;
    border-left: 1px solid ${styles.colors.darkGray};
    @media only screen and (max-width: 768px) {
        padding-left: 0;
        border-left: none;
        padding-top: 24px;
    }
`

const Section = styled.div`
    background-color: ${styles.colors.darkBackground};
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
`

const SectionTitle = styled.div`
    margin-bottom: 16px;
`

const Dot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${({ color }) => color || styles.colors.white};
    margin-right: 8px;
`

const ModelRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
`

/**
 * @param {{
 *   data: {
 *     make: string,
 *     make_friendly_name: string,
 *     model: string,
 *     model_friendly_name: string,
 *     id: number,
 *     version: string,
 *     first_detected_at: string,
 *     percent_of_fleet: number,
 *     is_latest: boolean,
 *     installed_on_models: string[],
 *     install_distribution_location: Array,
 *     install_distribution_by_country: Array,
 *     previous_versions: Array,
 *     rollout_by_date: Array,
 *     release_notes: Array,
 *     fsd_versions: string[],
 *     error: string
 *   }
 * }} props
 */
export default function SoftwareDetails({ data }) {
    const {
        make,
        make_friendly_name,
        model,
        model_friendly_name,
        id,
        installed_on_models,
        version,
        first_detected_at,
        percent_of_fleet,
        is_latest,
        install_distribution_location,
        install_distribution_by_country,
        previous_versions,
        rollout_by_date,
        release_notes,
        fsd_versions,
        error
    } = data
    const fleetPctText = useMemo(() => `of TezLab ${make_friendly_name} Fleet`, [make_friendly_name])
    const firstDetected = useMemo(() => !!first_detected_at ? `First Detected ${formatDate(first_detected_at)}` : 'Not Fully Installed Yet', [first_detected_at])

    const modelColors = useMemo(() => {
        const obj = {}
        installed_on_models?.forEach((model, i) => {
            obj[model] = colors[i]
        })
        return obj
    }, [installed_on_models])

    if (error) {
        return (
            <ScreenWrapper>
                <StyledErrorContainer>
                    <Text.Heading type='h1' variant='bold'>Error</Text.Heading>
                    <Text.Body>{error}</Text.Body>
                </StyledErrorContainer>
            </ScreenWrapper>
        )
    }

    return (
        <ScreenWrapper>
            <GeistFonts />
            <StyledContainer>
                <Row>
                    <Col xs={12} md={6} className='d-flex flex-column'>
                        <BackLink href={`/software-tracker/${make}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                <path d="M5.22033 7.33333L7.60699 4.94L6.66699 4L2.66699 8L6.66699 12L7.60699 11.06L5.22033 8.66667H13.3337V7.33333H5.22033Z" fill="currentColor" />
                            </svg>
                            Back
                        </BackLink>
                        <HeaderRow>
                            <Text.Heading type='h1' mobileL='h4' variant='bold' style={{ fontFamily: styles.fonts.geistMono }}>{version}</Text.Heading>
                        </HeaderRow>
                        {(fsd_versions || []).length > 0 && (
                            <Text.Caption color={styles.colors.lightGray} style={{ fontFamily: styles.fonts.geistMono, marginBottom: '4px' }}>
                                FSD <span style={{ color: '#c4b5fd' }}>{fsd_versions.join(', ')}</span>
                            </Text.Caption>
                        )}
                        <Text.Body color={styles.colors.lightGray}>{firstDetected}</Text.Body>
                    </Col>
                    <Col xs={12} md={6} className='d-flex justify-content-md-end'>
                        <FleetStat>
                            <Text.Heading type='ultra' variant='bold' style={{ marginBottom: '12px', fontFamily: styles.fonts.geistMono }} color={styles.colors.blue}>{percent_of_fleet === 0 ? '<0.1' : percent_of_fleet}%</Text.Heading>
                            <Text.Body variant='bold' color={styles.colors.lightGray}>{fleetPctText}</Text.Body>
                        </FleetStat>
                    </Col>
                </Row>

                {/* Release Notes */}
                {(release_notes || []).length > 0 && (
                    <Section style={{ marginTop: '32px' }}>
                        <SectionTitle>
                            <Text.Heading type='h3' variant='bold'>Release Notes</Text.Heading>
                        </SectionTitle>
                        <ReleaseNotes notes={release_notes} />
                    </Section>
                )}

                {/* Rollout Chart */}
                <Section style={{ marginTop: (release_notes || []).length > 0 ? 0 : '32px' }}>
                    <SectionTitle>
                        <Text.Heading type='h3' variant='bold' style={{ marginBottom: '4px' }}>Rollout</Text.Heading>
                        <Text.Caption color={styles.colors.lightGray}>How this software was adopted by the fleet based on date and model.</Text.Caption>
                    </SectionTitle>
                    <div style={{ marginBottom: '12px' }}>
                        {installed_on_models?.map((model, i) => (
                            <ModelRow key={model}>
                                <Dot color={modelColors[model]} />
                                <Text.Caption>{model}</Text.Caption>
                            </ModelRow>
                        ))}
                    </div>
                    <RolloutChart rollout={rollout_by_date} colors={modelColors} />
                </Section>

                {/* World Map */}
                <Section>
                    <SectionTitle>
                        <Text.Heading type='h3' variant='bold' style={{ marginBottom: '4px' }}>By Location</Text.Heading>
                        <Text.Caption color={styles.colors.lightGray}>Software version distribution by country</Text.Caption>
                    </SectionTitle>
                    {(install_distribution_by_country || []).length > 0 ? (
                        <WorldMap countries={install_distribution_by_country} />
                    ) : (
                        <Text.Caption color={styles.colors.mediumGray}>No location data available</Text.Caption>
                    )}
                </Section>

                {/* Previous Versions */}
                <Section>
                    <SectionTitle>
                        <Text.Heading type='h3' variant='bold' style={{ marginBottom: '4px' }}>Previously Installed</Text.Heading>
                        <Text.Caption color={styles.colors.lightGray}>Which software are people upgrading from</Text.Caption>
                    </SectionTitle>
                    <PreviousVersions versions={previous_versions} currentVersion={version} />
                </Section>
            </StyledContainer>
        </ScreenWrapper>
    )
}
